import { INSPECTION_VEHICLE_CLASSES } from './review.js';
import { deriveSignupModalities } from './signup.js';
/** The purposes a vehicle is registered for, in display order: the ride modalities (their own keys), then one per delivery module. */
export const DRIVER_VEHICLE_PURPOSES = ['RIDE_CAR', 'RIDE_MOTO', 'DELIVERY_SEND', 'DELIVERY_FOOD'];
/** The consumer module each delivery purpose stands for (`product.verticals`). */
const DELIVERY_PURPOSE_MODULE = { DELIVERY_SEND: 'send', DELIVERY_FOOD: 'food' };
/** D821: the purposes that carry PASSENGERS (the ride modalities) - every purpose that is not a delivery module. A vehicle registered for one of them takes rides. */
export const DRIVER_RIDE_VEHICLE_PURPOSES = DRIVER_VEHICLE_PURPOSES.filter((p) => !(p in DELIVERY_PURPOSE_MODULE));
const unique = (xs) => [...new Set(xs)];
/** The vehicle types the catalog's rule for `category` allows: its list; every class the inspection verifies when the rule names none; nothing when there is no rule. */
export function categoryAllowedVehicleTypes(catalog, category) {
    const rule = catalog?.vehicleRules.find((r) => r.category === category)?.rule;
    if (rule === undefined)
        return [];
    return rule.allowedVehicleTypes !== undefined ? [...rule.allowedVehicleTypes] : [...INSPECTION_VEHICLE_CLASSES];
}
/**
 * D854 (LT-001): the categories a market offers with NO category-catalog entry in force (fare-only) placed by the KYC catalog's vehicle rule: a fare-only category with a rule
 * becomes a mobility category served by the classes that rule allows; one without a rule stays out (nothing to place it by). A category that already has a catalog entry is never
 * duplicated. The ONE placement both the signed-in driver's modalities and the sign-up's modalities read, so the two never disagree about what a market runs.
 */
export function placeFareOnlyCategories(categories, fareOnly, catalog) {
    const placed = new Set(categories.map((c) => c.code));
    const fromRules = [...new Set(fareOnly)]
        .filter((code) => !placed.has(code))
        .flatMap((code) => {
        const classes = categoryAllowedVehicleTypes(catalog, code);
        return classes.length > 0 ? [{ code, kind: 'mobility', vehicleClasses: classes }] : [];
    });
    return [...categories, ...fromRules];
}
/**
 * The modalities of a driver's market with their purposes, categories and allowed vehicle types. Both personas are read (the sign-up's `driver.signup` personas decide who
 * may START a sign-up, not what a driver who is in may do).
 */
export function describeDriverModalities(input) {
    const facts = placeFareOnlyCategories(input.categories, input.fareOnly ?? [], input.catalog);
    const byCode = new Map(facts.map((c) => [c.code, c]));
    return deriveSignupModalities({ categories: facts, verticals: input.verticals, personas: ['driver', 'courier'] }).map((m) => {
        const categories = m.categories.map((code) => {
            const labels = byCode.get(code)?.labels;
            return { code, ...(labels !== undefined ? { labels } : {}), allowedVehicleTypes: categoryAllowedVehicleTypes(input.catalog, code) };
        });
        const purposes = m.key === 'DELIVERY'
            ? Object.keys(DELIVERY_PURPOSE_MODULE)
                .filter((p) => m.verticals.includes(DELIVERY_PURPOSE_MODULE[p]))
                .map((p) => ({ key: p, labelKey: `driver.purpose.${p}` }))
            : [{ key: m.key, labelKey: m.labelKey }];
        return {
            key: m.key,
            persona: m.persona,
            labelKey: m.labelKey,
            purposes,
            categories,
            verticals: m.verticals,
            allowedVehicleTypes: m.key === 'DELIVERY' ? [...INSPECTION_VEHICLE_CLASSES] : unique(categories.flatMap((c) => c.allowedVehicleTypes)),
        };
    });
}
/** Every purpose key the described modalities offer. */
export function offeredVehiclePurposes(modalities) {
    return modalities.flatMap((m) => m.purposes.map((p) => p.key));
}
//# sourceMappingURL=driver-modalities.js.map